import * as THREE from 'three/webgpu';
import { texture, uv, vec2, vec3, float, attribute, mix } from 'three/tsl';

import { uNight, uSunMix } from '../core/uniforms.js';
import {
  dayNightVertexColor,
  dayNightInstanceTint,
  sunShadeTint,
  warmGrade,
  swayNode,
  nightFadeFactor,
} from './nodes.js';

/**
 * @file Material FACTORIES. Every one of them returns a brand new material —
 * nothing is cached here on purpose: caching is the per-level
 * {@link import('../core/registry.js').Registry}'s job, because that is what
 * `Level.dispose()` walks. Call these inside `registry.material(key, () => ...)`.
 *
 * All materials are `MeshBasicNodeMaterial`: no lights, no shadow maps, ever.
 * Tone mapping is `NoToneMapping` (see `core/renderer.js`), so baked values
 * above 1 survive to the bloom threshold instead of being rolled off.
 *
 * ⚠ `vertexColors` is deliberately LEFT FALSE on every baked material — see the
 * long note at the top of `render/nodes.js`. r185's `NodeMaterial` multiplies
 * `colorNode` by the `color` attribute again when the flag is on.
 *
 * @module engine/render/materials
 */

/** @typedef {import('three/tsl').ShaderNodeObject<any>} TSLNode */
/** @typedef {THREE.Color|number|string|[number,number,number]} ColorLike */
/** @typedef {ReturnType<typeof import('./OcclusionFade.js').createOcclusionFade>} OcclusionFade */

/**
 * Fixed render order for the blended layers in the project. Opaque geometry
 * stays at 0 and relies on the depth buffer; these must draw in this order or
 * ground decals will paint over blob shadows.
 *
 * ⚠ Two coplanar transparent layers do NOT sort by insertion order — r185 sorts
 * them by `groupOrder → renderOrder → z`, and `z` is the geometry bounding-
 * sphere CENTRE, so two full-map ground layers flip and strobe as the camera
 * moves. Every extra ground layer therefore takes an EXPLICIT fractional slot.
 * The ones in use, in draw order:
 *
 *   decal            1     wear ink, projected hard shadows
 *   (city paint)     1.5   `levels/city/index.js` — road paint over the ink
 *   (city lanes)     1.6   `levels/city/index.js` — lane markings over the paint
 *   lightPool        1.8   headlight / brake pools (additive, over the paint so
 *                          the beam lights the markings it falls on)
 *   blobShadow       2     dynamic contact shadows — ON TOP of a light pool, so
 *                          an actor standing in a beam still casts into it
 *   (blood splats)   2.5   `render/Blood.js`
 *   marker           2.7   mission rings — over the blood and the shadows,
 *                          because a navigation aid that a corpse can hide is
 *                          not a navigation aid
 *   smoke            2.8   `render/VehicleFx.js` — a damaged car's plume. ABOVE
 *                          the marker because smoke is in the AIR over the ring
 *                          and has to read that way, and it is the first layer
 *                          in the table that is NOT coplanar with the ground
 *   fire             2.9   `render/VehicleFx.js` — the flame, additive, drawn
 *                          directly over its own soot. This is the one pair in
 *                          the table that must never swap: a flame sorted
 *                          BEHIND the smoke it is producing is a grey car fire
 *   emissive         3     window shells, tracers, droplets — over the flame,
 *                          so a tracer crossing a burning car stays a tracer
 *   speech           3.2   `render/SpeechBubbles.js` — NPC speech bubbles. Top
 *                          of the table on purpose: a bubble is a LEGIBILITY
 *                          surface, and a tracer or a fire puff painted across
 *                          the words is a line the player cannot read
 */
export const RENDER_ORDER = Object.freeze({
  sky: -1000,
  opaque: 0,
  decal: 1,
  lightPool: 1.8,
  blobShadow: 2,
  marker: 2.7,
  smoke: 2.8,
  fire: 2.9,
  emissive: 3,
  speech: 3.2,
});

/**
 * @param {ColorLike} c
 * @returns {[number, number, number]} Linear-ish RGB triple (no colour-space
 *   conversion: the bake works in the same space the materials output).
 */
function rgb(c) {
  if (Array.isArray(c)) return [c[0], c[1], c[2]];
  const col = c instanceof THREE.Color ? c : new THREE.Color(c);
  return [col.r, col.g, col.b];
}

/**
 * @param {number|[number,number]|null|undefined} uvScale
 * @returns {TSLNode} vec2
 */
function scaledUv(uvScale) {
  if (uvScale == null || uvScale === 1) return uv();
  if (Array.isArray(uvScale)) return uv().mul(vec2(uvScale[0], uvScale[1]));
  return uv().mul(float(uvScale));
}

/**
 * The workhorse: merged, CPU-baked level geometry with an optional tiling colour
 * map.
 *
 * `colorNode = texture(map, uv * uvScale).rgb * mix(color*warm, color1, uNight)`
 *
 * @param {Object} [o]
 * @param {THREE.Texture|null} [o.map] Colour map (already AO-multiplied offline
 *   for materials that tile at >= 2m/repeat — see the plan).
 * @param {number|[number,number]} [o.uvScale=1] UV repeat multiplier applied in
 *   the shader (cheaper than re-writing UVs, and lets one texture serve several
 *   scales).
 * @param {ColorLike|null} [o.tint] Constant multiplier on top of the bake.
 * @param {number} [o.alphaTest=0] >0 switches on cutout silhouettes (player 0.5,
 *   foliage 0.1). Uses the map's alpha; depth-writing stays ON, which is why
 *   foliage does not need sorting.
 * @param {{amount?:number, freq?:number, base?:number, phase?:TSLNode}|null} [o.sway]
 *   Foliage sway ({@link swayNode}); omit for anything rigid.
 * @param {THREE.Side} [o.side=THREE.FrontSide] `DoubleSide` for leaf cards.
 * @param {boolean} [o.fog=true]
 * @param {OcclusionFade|null} [o.fade] Dissolve this geometry when it stands
 *   between the camera and the player ({@link import('./OcclusionFade.js').createOcclusionFade}).
 *   The material stays OPAQUE — the fade is a screen-door discard, so nothing
 *   here touches `transparent` or the sort order.
 * @param {string} [o.name='baked']
 * @returns {THREE.MeshBasicNodeMaterial}
 */
export function bakedMaterial(o = {}) {
  const {
    map = null,
    uvScale = 1,
    tint = null,
    alphaTest = 0,
    sway = null,
    side = THREE.FrontSide,
    fog = true,
    fade = null,
    sun = false,
    name = 'baked',
  } = o;

  const m = new THREE.MeshBasicNodeMaterial({ side });
  m.name = name;
  m.fog = fog;
  // See the header note: NEVER true — colorNode already reads the attributes.
  m.vertexColors = false;

  const tex = map ? texture(map, scaledUv(uvScale)) : null;

  // ⚠ `sun: true` is only legal on geometry from a `splitDay` bake carrying
  // `color2`/`color3`. On anything else `vertexColor` falls back to WHITE and
  // the surface blows out in daylight — see `render/nodes.js`.
  let color = dayNightVertexColor({ sun });
  if (tex) color = tex.rgb.mul(color);
  if (tint) {
    const t = rgb(tint);
    color = color.mul(vec3(t[0], t[1], t[2]));
  }
  m.colorNode = color;

  if (alphaTest > 0) {
    m.alphaTest = alphaTest;
    if (tex) m.opacityNode = tex.a;
  }
  applyFade(m, fade);
  if (sway) m.positionNode = swayNode(sway);

  return m;
}

/**
 * Folds an occlusion fader's screen-door cut into a material's opacity.
 *
 * The cut is 0 or 1, so it composes with a cutout alpha by plain multiplication
 * and the shared `alphaTest` of 0.5 discards either way — a foliage card that
 * is also fading is still tested against its own map alpha first.
 *
 * @param {THREE.MeshBasicNodeMaterial} m
 * @param {OcclusionFade|null} fade
 */
function applyFade(m, fade) {
  if (!fade?.opacity) return;
  const cut = fade.opacity();
  m.opacityNode = m.opacityNode ? m.opacityNode.mul(cut) : cut;
  m.alphaTest = Math.max(m.alphaTest ?? 0, fade.alphaTest ?? 0.5);
}

/**
 * {@link bakedMaterial} plus a per-instance day/night ambient tint, for
 * `build/InstancedBatch.js`. The geometry keeps its own two-channel bake; the
 * instance attributes say how bright the SPOT that copy stands in is.
 *
 * Build the attribute nodes on the caller side, e.g.
 * `instancedBufferAttribute(iColD, 'vec3')`, and hand them in — this module
 * never touches buffer layout.
 *
 * @param {Object} o
 * @param {TSLNode|null} o.attrD Per-instance DAY tint (vec3).
 * @param {TSLNode|null} [o.attrN] Per-instance NIGHT tint (vec3); defaults to `attrD`.
 * @param {THREE.Texture|null} [o.map]
 * @param {number|[number,number]} [o.uvScale=1]
 * @param {ColorLike|null} [o.tint]
 * @param {number} [o.alphaTest=0]
 * @param {{amount?:number, freq?:number, base?:number, phase?:TSLNode}|null} [o.sway]
 * @param {THREE.Side} [o.side=THREE.FrontSide]
 * @param {boolean} [o.fog=true]
 * @param {OcclusionFade|null} [o.fade] Same contract as {@link bakedMaterial}.
 *   `positionWorld` already carries the instance matrix, so rooftop clutter
 *   dissolves with the building it is standing on and nothing per-instance is
 *   needed here.
 * @param {string} [o.name='bakedInstanced']
 * @returns {THREE.MeshBasicNodeMaterial}
 */
export function bakedInstancedMaterial(o = {}) {
  const {
    attrD = null,
    attrN = null,
    attrVis = null,
    map = null,
    uvScale = 1,
    tint = null,
    alphaTest = 0,
    sway = null,
    side = THREE.FrontSide,
    fog = true,
    fade = null,
    name = 'bakedInstanced',
  } = o;

  const m = new THREE.MeshBasicNodeMaterial({ side });
  m.name = name;
  m.fog = fog;
  m.vertexColors = false;

  const tex = map ? texture(map, scaledUv(uvScale)) : null;

  // `attrVis` is the per-instance KEYED sun visibility (vec2: key a, key b —
  // see `build/InstancedBatch.js#enableSunVis`). Only a batch that carries the
  // attribute may pass it: the shade replaces the frozen `sunTint` that used to
  // be baked into `iColD` at placement, so the two must never be combined or a
  // prop is shadowed twice.
  const shade = attrVis ? sunShadeTint(mix(attrVis.x, attrVis.y, uSunMix)) : null;
  let color = dayNightVertexColor().mul(dayNightInstanceTint(attrD, attrN, shade));
  if (tex) color = tex.rgb.mul(color);
  if (tint) {
    const t = rgb(tint);
    color = color.mul(vec3(t[0], t[1], t[2]));
  }
  m.colorNode = color;

  if (alphaTest > 0) {
    m.alphaTest = alphaTest;
    if (tex) m.opacityNode = tex.a;
  }
  applyFade(m, fade);
  if (sway) m.positionNode = swayNode(sway);

  return m;
}

/**
 * Lit-window shell. ONE extra quad per camera-facing facade, drawn over the
 * building with a window-mask atlas: invisible by day, a bloom source at night.
 *
 * `color   = tint * warmGrade * uNight * intensity * mask.rgb`
 * `opacity = mask.a * uNight`
 *
 * The double `uNight` (colour AND alpha) is intentional — it keeps windows dead
 * through dusk and then ramps hard, which is what makes the city "switch on".
 *
 * Caller must set `mesh.renderOrder = RENDER_ORDER.emissive`.
 *
 * @param {Object} o
 * @param {THREE.Texture} o.maskTex Window atlas: RGB = per-window colour
 *   variation, A = which pixels are glass. NearestFilter, no mipmaps.
 * @param {ColorLike} [o.color=[1,0.86,0.62]] Base lamp colour.
 * @param {number} [o.intensity=2.2] >1 on purpose: bloom fuel.
 * @param {number|[number,number]} [o.uvScale=1]
 * @param {boolean} [o.additive=false] Additive blending reads better on dense
 *   towers but blows out against a bright sky; off by default.
 * @param {THREE.Side} [o.side=THREE.DoubleSide] Facade quads are infinitely
 *   thin, so double-siding costs nothing and saves the caller from deriving the
 *   winding of a rotated footprint.
 * @param {OcclusionFade|null} [o.fade] Dim the windows of a building that is
 *   fading out. This material is ALREADY blended, so it takes the fader's
 *   SMOOTH factor rather than the screen-door cut — dithering a bloom source
 *   just makes it sparkle.
 * @param {string} [o.name='emissiveShell']
 * @returns {THREE.MeshBasicNodeMaterial}
 */
export function emissiveShellMaterial(o = {}) {
  const {
    maskTex,
    color = [1, 0.86, 0.62],
    intensity = 2.2,
    uvScale = 1,
    additive = false,
    side = THREE.DoubleSide,
    fade = null,
    name = 'emissiveShell',
  } = o;

  const m = new THREE.MeshBasicNodeMaterial({ side });
  m.name = name;
  m.transparent = true;
  m.depthWrite = false;
  m.fog = true;
  m.vertexColors = false;
  if (additive) m.blending = THREE.AdditiveBlending;

  const c = rgb(color);
  const base = vec3(c[0], c[1], c[2]).mul(warmGrade()).mul(uNight).mul(float(intensity));

  if (maskTex) {
    const tex = texture(maskTex, scaledUv(uvScale));
    m.colorNode = base.mul(tex.rgb);
    m.opacityNode = tex.a.mul(uNight);
  } else {
    m.colorNode = base;
    m.opacityNode = uNight;
  }
  if (fade?.factor) m.opacityNode = m.opacityNode.mul(fade.factor());

  return m;
}

/**
 * Painted shop-sign board, sampling one cell of `assets/signText.js`'s atlas.
 *
 * `colorNode = tex.rgb * warmGrade() * mix(day, night, uNight)`
 *
 * OPAQUE, depth-writing, front-sided — a sign is a solid board bolted to a
 * fascia, not a blended overlay, so it needs no `RENDER_ORDER` slot and cannot
 * join the ground layers' sorting problem.
 *
 * ⚠ This is NOT a baked material and that is the decision, not an oversight. A
 * sign is DELIBERATELY the one exterior surface in the city that carries its own
 * light: shop signs are illuminated, so instead of a baked day channel that goes
 * black at midnight this ramps from `day` to a slightly hotter `night`, which is
 * what makes a parade of shopfronts still read after dusk. The consequence is
 * that the geometry never goes through `bakeVertexLight` and must live on its
 * OWN mesh — see `levels/city/signs.js` and the note there about why that mesh
 * is also what keeps an eighth material slot off the chunk grid.
 *
 * `day` sits under the 0.85 day bloom threshold on purpose; `night` is allowed
 * just over 1 so a sign is a soft bloom source and not a lamp.
 *
 * @param {Object} o
 * @param {THREE.Texture} o.map The sign atlas. Cells are selected per quad by
 *   `build/uvtools.js#setUVRect`, so this material must NOT scale UVs.
 * @param {number} [o.day=0.82] Daytime multiplier.
 * @param {number} [o.night=1.08] Night multiplier — bloom fuel, gently.
 * @param {ColorLike|null} [o.tint=null] Optional overall grade.
 * @param {OcclusionFade|null} [o.fade] Same contract as {@link bakedMaterial}: a
 *   sign must dissolve with the building it is bolted to, or a fading shopfront
 *   leaves its name floating in mid air.
 * @param {string} [o.name='sign']
 * @returns {THREE.MeshBasicNodeMaterial}
 */
export function signMaterial(o = {}) {
  const { map = null, day = 0.82, night = 1.08, tint = null, fade = null, name = 'sign' } = o;

  const m = new THREE.MeshBasicNodeMaterial({ side: THREE.FrontSide });
  m.name = name;
  m.fog = true;
  m.vertexColors = false;

  // Both sides of the mix are vec3: `warmGrade()` is a vec3 and mixing it
  // against a bare float is a type error in TSL, not a silent broadcast. The
  // NIGHT side is deliberately un-graded — the warm gold is a SUNLIGHT grade,
  // and a sign at midnight is lit by its own tubes, not by the sun.
  const level = mix(warmGrade().mul(float(day)), vec3(night, night, night), uNight);
  let color = map ? texture(map, uv()).rgb.mul(level) : level;
  if (tint) {
    const t = rgb(tint);
    color = color.mul(vec3(t[0], t[1], t[2]));
  }
  m.colorNode = color;

  applyFade(m, fade);
  return m;
}

/**
 * The ground DecalLayer material: black ink with per-vertex alpha, no depth
 * write, no polygonOffset (WebGPU's polygon-offset semantics differ from
 * WebGL's — the layer is lifted +0.015m in Y instead).
 *
 * Alpha is scaled by `1 - uNight*nightFade`: with no sun there is no hard
 * shadow, so the projected shadow polygons have to melt away after dusk.
 *
 * ⚠ The geometry MUST carry a Float32 `alpha` attribute (itemSize 1). Without
 * it r185 warns once and the layer renders fully transparent.
 *
 * Caller must set `mesh.renderOrder = RENDER_ORDER.decal`.
 *
 * @param {Object} [o]
 * @param {THREE.Texture|null} [o.map] Optional stencil (crosswalk, crack); its
 *   ALPHA multiplies the vertex alpha.
 * @param {ColorLike} [o.color=[0,0,0]] Ink colour — black for shadows, but burn
 *   patches / rust want a tint.
 * @param {number} [o.nightFade=0.7]
 * @param {number|[number,number]} [o.uvScale=1]
 * @param {string} [o.alphaAttr='alpha']
 * @param {THREE.Side} [o.side=THREE.FrontSide] Decals face +Y; `DoubleSide` is
 *   the escape hatch when a generator cannot guarantee winding.
 * @param {string} [o.name='decal']
 * @returns {THREE.MeshBasicNodeMaterial}
 */
export function decalMaterial(o = {}) {
  const {
    map = null,
    color = [0, 0, 0],
    nightFade = 0.7,
    uvScale = 1,
    alphaAttr = 'alpha',
    side = THREE.FrontSide,
    name = 'decal',
  } = o;

  const m = new THREE.MeshBasicNodeMaterial({ side });
  m.name = name;
  m.transparent = true;
  m.depthWrite = false;
  m.fog = true;
  m.vertexColors = false;
  m.polygonOffset = false;

  const c = rgb(color);
  m.colorNode = vec3(c[0], c[1], c[2]);

  let a = attribute(alphaAttr, 'float');
  if (map) a = a.mul(texture(map, scaledUv(uvScale)).a);
  m.opacityNode = a.mul(nightFadeFactor(nightFade));

  return m;
}

/**
 * Soft round contact shadow under a dynamic actor. One instanced quad per actor
 * (T5's `render/ShadowBlobs.js`), lying flat just above the ground.
 *
 * Caller must set `mesh.renderOrder = RENDER_ORDER.blobShadow`.
 *
 * @param {Object} o
 * @param {THREE.Texture} o.tex Radial falloff; ALPHA channel is the mask.
 * @param {number} [o.opacity=0.5] Peak darkness at noon.
 * @param {number} [o.nightFade=0.6] Blobs keep a little presence under the moon.
 * @param {ColorLike} [o.color=[0,0,0]]
 * @param {string} [o.name='blobShadow']
 * @returns {THREE.MeshBasicNodeMaterial}
 */
export function blobShadowMaterial(o = {}) {
  const {
    tex,
    opacity = 0.5,
    nightFade = 0.6,
    color = [0, 0, 0],
    name = 'blobShadow',
  } = o;

  const m = new THREE.MeshBasicNodeMaterial();
  m.name = name;
  m.transparent = true;
  m.depthWrite = false;
  m.fog = true;
  m.vertexColors = false;

  const c = rgb(color);
  m.colorNode = vec3(c[0], c[1], c[2]);

  const mask = tex ? texture(tex).a : float(1);
  m.opacityNode = mask.mul(float(opacity)).mul(nightFadeFactor(nightFade));

  return m;
}

/**
 * The pool of light a headlight (or a brake lamp) throws on the ground. One
 * instanced quad per beam, `render/LightPools.js`.
 *
 * ADDITIVE, because light adds — a multiplied or alpha-blended pool would
 * DARKEN the road paint it falls on, which is exactly backwards. Colour is
 * per-instance (`iTint`) so warm-white cones and a red rear wash share one draw.
 *
 * `opacity = mask.a * iAlpha * uNight`. The `uNight` is what makes this free by
 * day: brake lamps are lit at noon too, and a red puddle under a braking car in
 * sunlight is a bug, not a feature.
 *
 * ⚠ `depthTest: false` — OWNER DECISION, and the one thing to understand here.
 * A ground quad that IS depth-tested gets eaten by the world it lies on: a car
 * on the verge throws a beam that sinks under the road slab beside it and the
 * whole thing is clipped at the kerb, at every rise in the grass, everywhere.
 * Draping the beam over sampled ground heights fixes that in principle and was
 * tried; it still broke in many places and cost ~7x the instances, so the beam
 * now ignores depth entirely and always draws whole.
 *
 * The price, so nobody rediscovers it as a bug: a beam is painted over
 * everything drawn before it, so it shines THROUGH a wall between the camera
 * and the road it lands on. It is cheap in practice because the layer is
 * additive and dim (~0.15) — it reads as a faint wash on the wall rather than a
 * hole in it — and because traffic only exists in a ring around the player.
 * `renderOrder` still orders it against the other blended layers: over the road
 * markings so it lights them, under the blob shadows so an actor standing in a
 * beam still casts into it.
 *
 * ⚠ `fog: false`. Fog LERPS a fragment toward the fog colour, and under additive
 * blending that adds the fog colour to the framebuffer instead of fading the
 * beam out — distant pools would glow BRIGHTER than near ones. The beams are
 * ~10 m long and always under the player, so there is nothing to fade anyway.
 * `render/Tracers.js` is off for the same reason.
 *
 * Caller must set `mesh.renderOrder = RENDER_ORDER.lightPool`.
 *
 * @param {Object} o
 * @param {THREE.Texture} o.tex Beam falloff; ALPHA channel is the mask.
 * @param {number} [o.opacity=1] Global strength, on top of the per-instance
 *   `iAlpha`. Under ADDITIVE blending this is mathematically interchangeable
 *   with scaling the tint — it is a separate dial because "how strong is the
 *   beam" is the knob that gets tuned, and folding it into a colour triple
 *   makes that a three-number edit.
 * @param {string} [o.name='lightPool']
 * @returns {THREE.MeshBasicNodeMaterial}
 */
export function lightPoolMaterial(o = {}) {
  const { tex, opacity = 1, name = 'lightPool' } = o;

  // DoubleSide: the rear wash is the same quad with a flipped axis, which
  // reverses its winding. Same escape hatch `decalMaterial` documents, and on a
  // flat two-triangle quad it costs nothing.
  const m = new THREE.MeshBasicNodeMaterial({ side: THREE.DoubleSide });
  m.name = name;
  m.transparent = true;
  m.depthWrite = false;
  // See the header. Never write depth either — that would break every layer
  // drawn after this one.
  m.depthTest = false;
  m.fog = false;
  m.vertexColors = false;
  m.blending = THREE.AdditiveBlending;

  m.colorNode = attribute('iTint', 'vec3');
  const mask = tex ? texture(tex).a : float(1);
  m.opacityNode = mask.mul(attribute('iAlpha', 'float')).mul(uNight).mul(float(opacity));

  return m;
}

/**
 * THE MISSION RING: the glowing circle you walk into. One instanced ground quad
 * per marker, `render/MissionMarkers.js`.
 *
 * It is {@link lightPoolMaterial}'s twin with two deliberate differences, and
 * both of them are the difference between a light and a SIGN:
 *
 *  • **No `uNight`.** A headlight pool that glows at noon is a bug; a mission
 *    marker that only glows at night is a marker you cannot find at noon. This
 *    one is on all day.
 *  • **It clears the DAY bloom threshold.** Bloom starts at 0.80 by day and
 *    0.45 at night (`render/post.js`), and the ring is additive over a road
 *    that is already lit — so a peak around 0.6 of its own puts a sunlit marker
 *    over the line and a midnight one well over it. That halo IS the marker at
 *    50 m; the geometry is only 6 m across.
 *
 * ⚠ `depthTest: false`, same as the light pools and for the same reason — a
 * depth-tested ground quad is eaten by every kerb it crosses. Here the known
 * price is a FEATURE rather than a cost: the ring shows faintly through the
 * building between you and it, which is exactly what a navigation aid should
 * do. It is why the marker is a ring and not a floodlit disc.
 *
 * ⚠ `fog: false` — additive + fog ADDS the fog colour instead of fading the
 * quad, so distant markers would glow brighter than near ones. Same trap
 * `lightPoolMaterial` and `render/Tracers.js` document.
 *
 * Caller must set `mesh.renderOrder = RENDER_ORDER.marker`.
 *
 * @param {Object} o
 * @param {THREE.Texture} o.tex Ring falloff; ALPHA channel is the mask.
 * @param {number} [o.opacity=1] Global strength on top of the per-instance
 *   `iAlpha` — the one knob worth tuning, exactly as in the light pools.
 * @param {string} [o.name='missionMarker']
 * @returns {THREE.MeshBasicNodeMaterial}
 */
export function missionMarkerMaterial(o = {}) {
  const { tex, opacity = 1, name = 'missionMarker' } = o;

  const m = new THREE.MeshBasicNodeMaterial({ side: THREE.DoubleSide });
  m.name = name;
  m.transparent = true;
  m.depthWrite = false;
  m.depthTest = false;
  m.fog = false;
  m.vertexColors = false;
  m.blending = THREE.AdditiveBlending;

  m.colorNode = attribute('iTint', 'vec3');
  const mask = tex ? texture(tex).a : float(1);
  m.opacityNode = mask.mul(attribute('iAlpha', 'float')).mul(float(opacity));

  return m;
}

/**
 * THE SMOKE a damaged car pours out. One instanced CAMERA-FACING quad per puff,
 * `render/VehicleFx.js`.
 *
 * {@link lightPoolMaterial}'s shape with three deliberate differences, and every
 * one of them is the difference between a LIGHT lying on the road and a SOLID
 * lump of soot standing in the air:
 *
 *  • **ALPHA-blended, not additive.** Smoke OCCLUDES. Additive smoke would add
 *    its grey to the road and read as fog lit from inside.
 *  • **`depthTest: true`.** The ground layers turn depth off because a quad
 *    lying on a heightfield is eaten by every kerb it crosses. A puff is a metre
 *    up in the air with nothing coplanar to fight, and it MUST be occluded by
 *    the building it drifts behind — a plume punching through a wall is far
 *    worse than a clipped kerb. `depthWrite` still off: puffs overlap each
 *    other constantly and writing depth would make them punch holes.
 *  • **`fog: true`.** Fog LERPS toward the fog colour, which is exactly right
 *    for an alpha-blended surface — a distant plume greys out with the city
 *    behind it. (The additive layers must opt OUT; see {@link firePuffMaterial}.)
 *
 * ⚠ **THE COLOUR IS CAPPED UNDER THE NIGHT BLOOM THRESHOLD, AND THE BLEND MODE
 * IS WHY THAT IS ENOUGH.** Bloom starts at 0.80 by day and 0.45 at night
 * (`render/post.js`). An alpha blend is a CONVEX COMBINATION — the result can
 * never exceed the brighter of the puff and what is behind it — so as long as
 * `VehicleFx.js#FX.smokePale` stays under 0.45 (it is 0.40) smoke can only ever
 * pull a pixel DOWN toward itself. A smoking car therefore cannot light up the
 * street at midnight, whatever the emitter does with `alpha`. The same is not
 * true of an additive layer, which is the whole reason these are two materials.
 *
 * Caller must set `mesh.renderOrder = RENDER_ORDER.smoke` (2.8) and supply the
 * per-instance `iTint` (vec3, linear) and `iAlpha` (float) attributes, plus a
 * `positionNode` — the billboarding is the batch's job, not this file's, which
 * is what keeps `materials.js` camera-free and node-constructible.
 *
 * @param {Object} o
 * @param {THREE.Texture} o.tex Soft radial falloff; ALPHA channel is the mask.
 *   `ShadowBlobs.js#makeFalloffTexture` — do not build a second one.
 * @param {number} [o.opacity=1] Global strength on top of the per-instance
 *   `iAlpha`. Under an ALPHA blend this is NOT interchangeable with scaling the
 *   tint (that would darken the puff instead of thinning it), so unlike the
 *   additive layers it is the only honest place to put "how thick is the smoke".
 * @param {string} [o.name='smokePuff']
 * @returns {THREE.MeshBasicNodeMaterial}
 */
export function smokePuffMaterial(o = {}) {
  const { tex, opacity = 1, name = 'smokePuff' } = o;

  // DoubleSide: the quad's winding depends on the handedness of the camera
  // basis the batch feeds its `positionNode`, so there is no fixed front face
  // to cull to. Same escape hatch `decalMaterial` and `render/weather.js` take.
  const m = new THREE.MeshBasicNodeMaterial({ side: THREE.DoubleSide });
  m.name = name;
  m.transparent = true;
  m.depthWrite = false;
  m.depthTest = true;
  m.fog = true;
  m.vertexColors = false;

  m.colorNode = attribute('iTint', 'vec3');
  // The falloff carries the puff shape in ALPHA only, and it has to ride
  // `opacityNode`: assigning `.map`/`.alphaMap` does NOTHING once `colorNode`/
  // `opacityNode` are set, because a NodeMaterial only synthesises those from
  // the legacy slots when the node is null. (Same trap `render/Blood.js` logs.)
  const mask = tex ? texture(tex).a : float(1);
  m.opacityNode = mask.mul(attribute('iAlpha', 'float')).mul(float(opacity));

  return m;
}

/**
 * THE FLAME, and the fireball. {@link smokePuffMaterial}'s twin, ADDITIVE.
 *
 * Fire is light, so it adds — and that single fact drives everything else here:
 *
 *  • **`fog: false`.** Fog ADDS its colour to the framebuffer under an additive
 *    blend instead of fading the quad out, so a distant fire would glow
 *    BRIGHTER than a near one. Same trap `lightPoolMaterial`,
 *    `missionMarkerMaterial` and `render/Tracers.js` all document.
 *  • **`depthTest: true`, `depthWrite: false`** — as the smoke, and for the
 *    same reasons. Unlike the ground layers a flame is genuinely in the world
 *    and a wall in front of it must hide it.
 *
 * ⚠ **THE COLOUR MUST CLEAR THE DAY BLOOM THRESHOLD ON ITS OWN.** Bloom starts
 * at 0.80 by day (`render/post.js`), and the halo IS the fire at 40 m — the
 * geometry is a metre across. `VehicleFx.js#FX.fireCool`'s red is 1.30, so one
 * puff clears 0.80 from `alpha` 0.62 upward, in full sunlight, over black.
 *
 * ⚠ **AND IT OVERLAPS ITSELF.** Several puffs on one bonnet SUM, so the number
 * that hits the framebuffer is `nPuffs * tint * iAlpha * opacity`, never one
 * puff's tint. That is a feature (the core of a fire saturates to white) and a
 * budget: see the arithmetic in `VehicleFx.js#FX`.
 *
 * Caller must set `mesh.renderOrder = RENDER_ORDER.fire` (2.9) — directly over
 * the smoke, so the flame is never sorted behind its own soot.
 *
 * @param {Object} o
 * @param {THREE.Texture} o.tex Soft radial falloff; ALPHA channel is the mask.
 * @param {number} [o.opacity=1] Global strength on top of the per-instance
 *   `iAlpha`. Under ADDITIVE blending this is mathematically interchangeable
 *   with scaling the tint — it is a separate dial for the same reason
 *   {@link lightPoolMaterial}'s is: "how fierce is the fire" should be one
 *   number to tune, not a three-number edit.
 * @param {string} [o.name='firePuff']
 * @returns {THREE.MeshBasicNodeMaterial}
 */
export function firePuffMaterial(o = {}) {
  const { tex, opacity = 1, name = 'firePuff' } = o;

  const m = new THREE.MeshBasicNodeMaterial({ side: THREE.DoubleSide });
  m.name = name;
  m.transparent = true;
  m.depthWrite = false;
  m.depthTest = true;
  m.fog = false;
  m.vertexColors = false;
  m.blending = THREE.AdditiveBlending;

  m.colorNode = attribute('iTint', 'vec3');
  const mask = tex ? texture(tex).a : float(1);
  m.opacityNode = mask.mul(attribute('iAlpha', 'float')).mul(float(opacity));

  return m;
}

/**
 * Untextured baked geometry — painted metal, plastic, glass, terrain that is
 * pure vertex colour. Identical to {@link bakedMaterial} with no map; exists so
 * call sites read as intent rather than as an omission.
 *
 * @param {Object} [o]
 * @param {ColorLike} [o.color=0xffffff] Constant multiplier over the bake.
 * @param {number} [o.alphaTest=0]
 * @param {THREE.Side} [o.side=THREE.FrontSide]
 * @param {{amount?:number, freq?:number, base?:number, phase?:TSLNode}|null} [o.sway]
 * @param {boolean} [o.fog=true]
 * @param {string} [o.name='flat']
 * @returns {THREE.MeshBasicNodeMaterial}
 */
export function flatMaterial(o = {}) {
  const { color = 0xffffff, ...rest } = o;
  return bakedMaterial({ name: 'flat', ...rest, tint: color, map: null });
}
